import React, { useEffect, useRef } from "react";

import { ospThemes } from "@ospweb/sdk-ui";
import { Kline } from "@/src/features/api/openApi";

interface KlineProps {
  data: Kline[];
  width?: number;
  height?: number;
  limit?: number;
  change?: number;
  excludeWhiteColor?: boolean;
  customKlineStyle?: {
    redColor?: string;
    greenColor?: string;
    grayColor?: string;
    lineWidth?: number;
    gradientStopOpacity?: number;
  };
}

export const KlineColumn: React.FC<KlineProps> = ({
  data,
  width = 170,
  height = 38,
  limit = 144, // token 默认不少于144条数据
  change,
  excludeWhiteColor = false,
  customKlineStyle,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const d3 = typeof window !== "undefined" ? (window as any).d3 : undefined;
  const canUseD3 = !!d3?.select;

  const redColor =
    customKlineStyle?.redColor || ospThemes.colors.border_accent1_solid;
  const greenColor =
    customKlineStyle?.greenColor || ospThemes.colors.border_accent2_solid;
  const grayColor =
    customKlineStyle?.grayColor || ospThemes.colors.content_secondary;
  const lineWidth = customKlineStyle?.lineWidth || 1.5;

  useEffect(() => {
    if (!canUseD3 || !data || !svgRef.current || data.length < limit) {
      return;
    }

    // 计算当前宽度
    const currentWidth = width;
    d3.select(svgRef.current).selectAll("*").remove();

    const startPrice = data[0]?.price;
    const endPrice = data[data.length - 1]?.price;

    // 判断趋势（通过比较首尾价格）
    const isUpTrend =
      data.length > 0 && startPrice !== undefined && endPrice !== undefined
        ? startPrice <= endPrice
        : false;

    const trendColor = () => {
      if (change !== undefined) {
        if (excludeWhiteColor) {
          return change >= 0 ? redColor : greenColor;
        }
        return change > 0 ? redColor : change < 0 ? greenColor : grayColor;
      }

      return isUpTrend ? redColor : greenColor;
    };

    const isRising = change !== undefined ? change >= 0 : isUpTrend;
    const currentTrendColor = trendColor();
    const isWhiteColor = currentTrendColor === grayColor;
    // 设置边距
    const margin = { top: 5, right: 5, bottom: 5, left: 5 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // 创建SVG
    const svg = d3
      .select(svgRef.current)
      .attr("width", currentWidth)
      .attr("height", height)
      .attr("viewBox", `0 0 ${currentWidth} ${height}`)
      .attr("fill", "none")
      .attr("xmlns", "http://www.w3.org/2000/svg");

    // 创建渐变
    const gradientId = `area-gradient-${isWhiteColor ? "white" : isRising ? "up" : "down"}`;
    const gradient = svg
      .append("defs")
      .append("linearGradient")
      .attr("id", gradientId)
      .attr("x1", "0%")
      .attr("y1", "0%")
      .attr("x2", "0%")
      .attr("y2", "100%");

    if (isWhiteColor) {
      // 白色线条的渐变
      gradient
        .append("stop")
        .attr("offset", "0%")
        .attr("stop-color", grayColor)
        .attr("stop-opacity", 0.15);

      gradient
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", grayColor)
        .attr("stop-opacity", 0.02);
    } else {
      // 原有的上涨/下跌渐变
      gradient
        .append("stop")
        .attr("offset", "0%")
        .attr("stop-color", currentTrendColor)
        .attr("stop-opacity", customKlineStyle?.gradientStopOpacity || 0.3);

      gradient
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", currentTrendColor)
        .attr("stop-opacity", 0);
    }

    // 计算价格范围
    const prices = data
      .map((d) => Number(d.price))
      .filter((price) => !isNaN(price));
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    // 设置 y 轴比例尺
    const yScale = d3
      .scaleLinear()
      .domain([minPrice, maxPrice])
      .range([innerHeight, 0]);

    // 创建 x 轴比例尺
    const xScale = d3
      .scaleTime()
      .domain(
        d3.extent(
          data,
          (d: { timestamp: number }) => new Date(d.timestamp),
        ) as [Date, Date],
      )
      .range([0, innerWidth]);

    // 创建图表区域
    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // 创建面积生成器
    const area = d3
      .area()
      .x((d: { timestamp: number }) => xScale(new Date(d.timestamp)))
      .y1((d: { price: number }) => yScale(d.price))
      .y0(innerHeight)
      .curve(d3.curveCatmullRom.alpha(0.5));

    // 创建曲线生成器
    const line = d3
      .line()
      .x((d: { timestamp: number }) => xScale(new Date(d.timestamp)))
      .y((d: { price: number }) => yScale(d.price))
      .curve(d3.curveCatmullRom.alpha(0.5));

    // 绘制渐变区域
    g.append("path")
      .datum(data)
      .attr("fill", `url(#${gradientId})`)
      .attr("fill-rule", "evenodd")
      .attr("clip-rule", "evenodd")
      .attr("d", area);

    // 绘制线条
    g.append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", currentTrendColor)
      .attr("stroke-width", lineWidth)
      .attr("stroke-linecap", "round")
      .attr("stroke-linejoin", "round")
      .attr("d", line);
  }, [data, width, height, d3, canUseD3, limit]);

  /** 数据不足144条时，不显示, 144条数据为6天数据, 要有第七天的数据才显示折线图 */
  if (data.length < limit || !canUseD3) {
    return <div className="text-content_primary">--</div>;
  }

  return <svg ref={svgRef} />;
};
